import {
  PrimaryButton,
  Section,
  SectionContent,
  SectionDescription,
  SectionTitle,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getCurrentQuote,
  getUsablePaymentMethod,
  PaymentMethodType,
} from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { QuotePremiums, useQuotePremiums } from '../../../../../hooks/premiums'
import {
  useStripeCheckoutAdmin,
  useStripeCheckoutUser,
} from '../../../../../hooks/stripeCheckout'
import {
  TKey,
  tPaymentMethods,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { CheckoutMethod, PaymentScheduleConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { usePaymentMethods } from '../../../policy/billing'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../proposalContext'
import { CheckoutPath } from '../CheckoutPath'
import { DirectDebitSetup } from './DirectDebitSetup'

export interface CheckoutDualProps {
  checkoutMethod: CheckoutMethod
  config: PaymentScheduleConfig
}

const tKey = TKey('components.checkout-dual')

export const CheckoutDual: FunctionComponent<CheckoutDualProps> = ({
  config,
  checkoutMethod,
}) => {
  const { t } = useWriskTranslation()

  const { apiClient, isAdmin } = usePrincipal()

  const { proposal, setProposal, refresh } = useProposal()
  const { product } = useProduct()
  const { paymentMethods } = usePaymentMethods()

  const quote = getCurrentQuote(proposal)

  const paymentMethod = getUsablePaymentMethod(
    paymentMethods,
    PaymentMethodType.BACS_DEBIT,
  )
  const premiums = useQuotePremiums(quote, product, config.type)

  const checkoutType = getCheckoutType(premiums)

  const onStripeUserCheckout = useStripeCheckoutUser({
    saveCard: false,
    proposalCode: proposal.proposalCode,
    successPath: [location.pathname, CheckoutPath.CHECKOUT_CALLBACK].join('/'),
    cancelPath: location.pathname,
  })

  const onStripeAdminCheckout = useStripeCheckoutAdmin({
    saveCard: false,
    proposalCode: proposal.proposalCode,
    onSuccess: refresh,
  })

  const onCheckout = useApiErrorHandlingAsyncCallback(async () => {
    switch (checkoutType) {
      case 'PAY':
        return isAdmin
          ? await onStripeAdminCheckout.execute()
          : await onStripeUserCheckout.execute()
      case 'BIND':
        return setProposal(
          await apiClient.bindProposal(proposal.proposalCode, {
            paymentMethodType: PaymentMethodType.BACS_DEBIT,
          }),
        )
    }
  })

  return (
    <>
      <Section>
        <SectionTitle>{t(tKey('direct-debit', 'title'))}</SectionTitle>
        <SectionDescription>
          {t(
            [
              tKey('direct-debit', 'description', config.type, checkoutType),
              tKey('direct-debit', 'description', config.type),
              tKey('direct-debit', 'description'),
            ],
            {
              statement: t(tPaymentMethods('statement')),
            },
          )}
        </SectionDescription>
        <SectionContent>
          <DirectDebitSetup mandate={paymentMethod} context={checkoutMethod} />
        </SectionContent>
      </Section>

      {['PAY'].includes(checkoutType) && (
        <Section>
          <SectionTitle>{t(tKey('deposit', 'title'))}</SectionTitle>
          <SectionDescription>
            {t([
              tKey('deposit', 'description', config.type, checkoutType),
              tKey('deposit', 'description', config.type),
              tKey('deposit', 'description'),
            ])}
          </SectionDescription>
        </Section>
      )}

      <PrimaryButton
        data-testid='bind-proposal'
        layout='fixed'
        disabled={!paymentMethod}
        loading={onCheckout.loading}
        onClick={onCheckout.execute}
      >
        {t([
          tKey('action', config.type, checkoutType),
          tKey('action', config.type),
          tKey('action'),
        ])}
      </PrimaryButton>
    </>
  )
}

const getCheckoutType = (premiums: QuotePremiums): 'PAY' | 'BIND' =>
  premiums.totals.discounted > 0.01 ? 'PAY' : 'BIND'
